'use strict'
import React, { useCallback, useMemo, useState } from 'react'
import axios from 'axios'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  TextField,
  CircularProgress,
  Typography,
  FormHelperText
} from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CutCrewImpactSummary from './CutCrewImpactSummary'
import CutCrewPreviewTable from './CutCrewPreviewTable'

const STEPS = ['Select Route', 'Preview & Apply']

const STRATEGY_OPTIONS = [
  { value: 'leave_unassigned', label: 'Leave all displaced unassigned' },
  { value: 'capacity_balance', label: 'Auto-balance by capacity' },
  { value: 'nearest_first', label: 'Nearest active crew first' }
]

export default function CutCrewModal ({ open, onClose, url, scenarioId, routes, onApplied }) {
  const [activeStep, setActiveStep] = useState(0)
  const [selectedRouteId, setSelectedRouteId] = useState('')
  const [strategy, setStrategy] = useState('leave_unassigned')
  const [prioritizeVip, setPrioritizeVip] = useState(true)
  const [respectCapacity, setRespectCapacity] = useState(true)
  const [recomputeMetrics, setRecomputeMetrics] = useState(true)
  const [maxWeeklyMinutes, setMaxWeeklyMinutes] = useState(2400)

  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewData, setPreviewData] = useState(null)
  const [previewError, setPreviewError] = useState(null)

  const [applyLoading, setApplyLoading] = useState(false)
  const [applyError, setApplyError] = useState(null)

  const [routeMetrics, setRouteMetrics] = useState(null)

  const activeRoutes = useMemo(() =>
    (routes || []).filter(r => r.crewId),
    [routes]
  )

  React.useEffect(() => {
    if (!open || !scenarioId) return
    let cancelled = false
    axios.get(`${url}/api/hmi/territory/scenarios/${scenarioId}/metrics`)
      .then(res => {
        if (cancelled) return
        const crewMetrics = res.data?.crews || []
        const byCrewId = new Map(crewMetrics.map(c => [c.crewId, c]))
        setRouteMetrics(byCrewId)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [open, scenarioId, url])

  const resetState = useCallback(() => {
    setActiveStep(0)
    setSelectedRouteId('')
    setStrategy('leave_unassigned')
    setPrioritizeVip(true)
    setRespectCapacity(true)
    setRecomputeMetrics(true)
    setMaxWeeklyMinutes(2400)
    setPreviewData(null)
    setPreviewError(null)
    setPreviewLoading(false)
    setApplyLoading(false)
    setApplyError(null)
    setRouteMetrics(null)
  }, [])

  const handleClose = useCallback(() => {
    resetState()
    onClose()
  }, [onClose, resetState])

  const buildPayload = useCallback(() => ({
    cutRouteId: selectedRouteId,
    strategy,
    prioritizeVip,
    respectCapacity,
    maxWeeklyMinutesPerCrew: Number(maxWeeklyMinutes) || 2400,
    recomputeMetrics
  }), [selectedRouteId, strategy, prioritizeVip, respectCapacity, maxWeeklyMinutes, recomputeMetrics])

  const handlePreview = useCallback(async () => {
    setPreviewLoading(true)
    setPreviewError(null)
    try {
      const response = await axios.post(
        `${url}/api/hmi/territory/scenarios/${scenarioId}/cut-crew/preview`,
        buildPayload()
      )
      setPreviewData(response.data)
      setActiveStep(1)
    } catch (err) {
      setPreviewError(err.response?.data?.message || err.message || 'Preview failed')
    } finally {
      setPreviewLoading(false)
    }
  }, [url, scenarioId, buildPayload])

  const handleApply = useCallback(async () => {
    setApplyLoading(true)
    setApplyError(null)
    try {
      const response = await axios.post(
        `${url}/api/hmi/territory/scenarios/${scenarioId}/cut-crew`,
        buildPayload()
      )
      const summary = response.data?.summary
      const message = summary
        ? `Crew cut applied: ${summary.displacedCount} displaced, ${summary.reassignedCount} reassigned, ${summary.unassignedCount} unassigned.`
        : 'Crew cut applied.'
      handleClose()
      if (onApplied) onApplied(message, response.data)
    } catch (err) {
      setApplyError(err.response?.data?.message || err.message || 'Apply failed')
      setApplyLoading(false)
    }
  }, [url, scenarioId, buildPayload, handleClose, onApplied])

  const renderStepper = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, paddingBottom: 16 }}>
      {STEPS.map((label, idx) => {
        const isDone = idx < activeStep
        const isActive = idx === activeStep
        return (
          <React.Fragment key={label}>
            {idx > 0 && (
              <div className={'step-connector' + (isDone ? ' done' : '')} />
            )}
            <div className={'step-node' + (isActive ? ' active' : '') + (isDone ? ' done' : '')}>
              {isDone
                ? <CheckCircleIcon style={{ fontSize: 14 }} />
                : <span style={{ fontSize: 11, fontWeight: 800 }}>{idx + 1}</span>
              }
              <span style={{ fontSize: 12 }}>{label}</span>
            </div>
          </React.Fragment>
        )
      })}
    </div>
  )

  const renderSelectStep = () => (
    <>
      <TableContainer style={{ maxHeight: 320, overflow: 'auto', marginBottom: 16, borderRadius: 8, border: '1px solid var(--df-line, #e0d8c2)' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" style={{ background: 'var(--df-surface-2, #f4efe4)', fontWeight: 700, fontSize: 11, color: 'var(--df-ink-3)' }} />
              <TableCell style={{ background: 'var(--df-surface-2, #f4efe4)', fontWeight: 700, fontSize: 11, color: 'var(--df-ink-3)' }}>Route Name</TableCell>
              <TableCell style={{ background: 'var(--df-surface-2, #f4efe4)', fontWeight: 700, fontSize: 11, color: 'var(--df-ink-3)' }}>Crew Leader</TableCell>
              <TableCell style={{ background: 'var(--df-surface-2, #f4efe4)', fontWeight: 700, fontSize: 11, color: 'var(--df-ink-3)' }}>Linked Crew</TableCell>
              <TableCell align="right" style={{ background: 'var(--df-surface-2, #f4efe4)', fontWeight: 700, fontSize: 11, color: 'var(--df-ink-3)' }}>Properties</TableCell>
              <TableCell align="right" style={{ background: 'var(--df-surface-2, #f4efe4)', fontWeight: 700, fontSize: 11, color: 'var(--df-ink-3)' }}>Weekly Min</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeRoutes.map(route => {
              const metrics = routeMetrics?.get(route.crewId)
              const isSelected = selectedRouteId === route._id
              return (
                <TableRow
                  key={route._id}
                  hover
                  selected={isSelected}
                  onClick={() => setSelectedRouteId(route._id)}
                  style={{
                    cursor: 'pointer',
                    background: isSelected ? 'var(--df-primary-soft, #e3eee0)' : undefined
                  }}
                >
                  <TableCell padding="checkbox">
                    <Radio
                      checked={isSelected}
                      onChange={() => setSelectedRouteId(route._id)}
                      size="small"
                      style={{ color: 'var(--df-primary, #1f5e3a)' }}
                    />
                  </TableCell>
                  <TableCell style={{ fontWeight: 600, fontSize: 12 }}>{route.name}</TableCell>
                  <TableCell style={{ fontSize: 12 }}>{route.crewLeader || '—'}</TableCell>
                  <TableCell style={{ fontSize: 12 }}>{route.crewName || '—'}</TableCell>
                  <TableCell align="right" style={{ fontSize: 12 }}>{metrics?.propertyCount ?? '—'}</TableCell>
                  <TableCell align="right" style={{ fontSize: 12 }}>{metrics?.weeklyServiceMinutes ?? '—'}</TableCell>
                </TableRow>
              )
            })}
            {activeRoutes.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" style={{ padding: 24, color: 'var(--df-ink-4)' }}>
                  No active routes with linked crews
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginTop: 16 }}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
          <InputLabel>Strategy</InputLabel>
          <Select value={strategy} onChange={e => setStrategy(e.target.value)} label="Strategy">
            {STRATEGY_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
          <FormHelperText style={{ fontSize: 10.5, color: 'var(--df-ink-4)' }}>
            Default keeps every displaced property unassigned for manual review.
          </FormHelperText>
        </FormControl>
        <FormControlLabel
          control={<Checkbox checked={prioritizeVip} onChange={e => setPrioritizeVip(e.target.checked)} size="small" style={{ color: 'var(--df-primary, #1f5e3a)' }} />}
          label={<span style={{ fontSize: 12 }}>Prioritize VIP</span>}
        />
        <FormControlLabel
          control={<Checkbox checked={respectCapacity} onChange={e => setRespectCapacity(e.target.checked)} size="small" style={{ color: 'var(--df-primary, #1f5e3a)' }} />}
          label={<span style={{ fontSize: 12 }}>Respect capacity</span>}
        />
        <FormControlLabel
          control={<Checkbox checked={recomputeMetrics} onChange={e => setRecomputeMetrics(e.target.checked)} size="small" style={{ color: 'var(--df-primary, #1f5e3a)' }} />}
          label={<span style={{ fontSize: 12 }}>Recompute metrics</span>}
        />
        <TextField
          label="Max weekly min"
          type="number"
          variant="outlined"
          size="small"
          style={{ width: 140 }}
          value={maxWeeklyMinutes}
          onChange={e => setMaxWeeklyMinutes(e.target.value)}
        />
      </div>
      {previewError && (
        <Typography variant="body2" style={{ marginTop: 16, color: '#a9341d', fontWeight: 600 }}>{previewError}</Typography>
      )}
    </>
  )

  const renderPreviewStep = () => (
    <>
      <CutCrewImpactSummary
        cutRoute={previewData?.cutRoute}
        displaced={previewData?.displaced}
        suggestedMovesCount={previewData?.suggestedMoves?.length || 0}
        unassignedCount={previewData?.unassigned?.length || 0}
        warnings={previewData?.warnings}
      />
      <CutCrewPreviewTable
        suggestedMoves={previewData?.suggestedMoves}
        unassigned={previewData?.unassigned}
      />
      {applyError && (
        <Typography variant="body2" style={{ marginTop: 16, color: '#a9341d', fontWeight: 600 }}>{applyError}</Typography>
      )}
    </>
  )

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle style={{ borderBottom: '1px solid var(--df-line, #e0d8c2)', paddingBottom: 8 }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--df-ink, #1c2419)' }}>Cut Crew</span>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 16 }}>
        {renderStepper()}
        {activeStep === 0 && renderSelectStep()}
        {activeStep === 1 && renderPreviewStep()}
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid var(--df-line, #e0d8c2)', padding: '12px 24px' }}>
        {activeStep === 0 && (
          <>
            <Button onClick={handleClose} style={{ color: 'var(--df-ink-3)' }}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePreview}
              disabled={!selectedRouteId || previewLoading}
              style={{ fontWeight: 600 }}
            >
              {previewLoading ? <CircularProgress size={20} style={{ color: '#fff' }} /> : 'Preview Impact'}
            </Button>
          </>
        )}
        {activeStep === 1 && (
          <>
            <Button onClick={handleClose} style={{ color: 'var(--df-ink-3)' }}>Cancel</Button>
            <Button
              variant="outlined"
              onClick={() => { setActiveStep(0); setPreviewError(null) }}
              style={{ borderColor: 'var(--df-line-2)', color: 'var(--df-ink-3)' }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApply}
              disabled={applyLoading}
              style={{ fontWeight: 600 }}
            >
              {applyLoading ? <CircularProgress size={20} style={{ color: '#fff' }} /> : 'Apply Cut Crew'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
